import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useRequestExpertReviewMutation, useScamCheckResult } from '@/hooks/useScamCheck'
import { queuePendingTextScamCheck } from '@/lib/scamCheckPending'
import { BrandedSpinner } from '@/components/BrandedSpinner'
import VerdictHero from '@/components/scam-check/VerdictHero'
import SubmittedUrlCard from '@/components/scam-check/SubmittedUrlCard'
import ObservationSection from '@/components/scam-check/ObservationSection'
import ShareReportRow from '@/components/scam-check/ShareReportRow'
import ExpertReviewRequestModal from '@/components/scam-check/ExpertReviewRequestModal'
import RedFlagsSection from '@/components/scam-check/RedFlagsSection'
import TrustSignalsSection from '@/components/scam-check/TrustSignalsSection'
import BrandComparison from '@/components/scam-check/BrandComparison'
import ActionStepsSection from '@/components/scam-check/ActionStepsSection'
import VideoAnalysisSection from '@/components/scam-check/VideoAnalysisSection'
import { FiArrowLeft, FiFlag, FiMail, FiMessageSquare, FiSmartphone, FiUser, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi'

export const Route = createFileRoute('/_auth/tools/scam-check/$checkId')({
  component: ScamCheckResultPage,
})

function ScamCheckResultPage() {
  const { checkId } = Route.useParams()
  const router = useRouter()
  const { data, isLoading, error } = useScamCheckResult(checkId)
  const [isVisible, setIsVisible] = useState(false)
  const [expertReviewRequested, setExpertReviewRequested] = useState(false)
  const [showExpertModal, setShowExpertModal] = useState(false)
  const [expertReviewError, setExpertReviewError] = useState<string | null>(null)
  const expertReviewMutation = useRequestExpertReviewMutation()

  useEffect(() => {
    const timer = window.setTimeout(() => setIsVisible(true), 30)
    return () => window.clearTimeout(timer)
  }, [])

  const handleRequestExpertReview = () => {
    setExpertReviewError(null)
    setShowExpertModal(true)
  }

  const handleConfirmExpertReview = async () => {
    setExpertReviewError(null)
    try {
      await expertReviewMutation.mutateAsync(checkId)
      setExpertReviewRequested(true)
      // Keep modal open and show success state.
    } catch {
      setExpertReviewError('Could not send this request right now. Please try again in a moment.')
    }
  }

  const handleRerun = () => {
    if (!data || data.inputType === 'IMAGE' || data.inputType === 'VIDEO') return
    // Map legacy EMAIL to TEXT/message; URL stays as link
    const mode = data.inputType === 'URL' ? 'link' as const : 'message' as const
    const inputType = data.inputType === 'URL' ? 'URL' as const : 'TEXT' as const
    const pendingRequest = queuePendingTextScamCheck({
      input: data.inputValue,
      inputType,
      mode,
    })
    router.navigate({
      to: '/tools/scam-check/processing',
      search: {
        requestId: pendingRequest.id,
        invitationcode: undefined,
        __clerk_ticket: undefined,
      },
    })
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white flex items-center justify-center">
        <div className="text-center">
          <BrandedSpinner size="lg" />
          <p className="mt-4 text-base text-slate-500">Loading your report...</p>
        </div>
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-white flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-5">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Report not found</h1>
          <p className="text-slate-500 mb-6">This scam check result could not be loaded.</p>
          <Link
            to="/tools"
            search={{ invitationcode: undefined, __clerk_ticket: undefined }}
            className="inline-flex items-center gap-2 text-violet-600 font-medium hover:text-violet-700"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Tools
          </Link>
        </div>
      </div>
    )
  }

  // When something is wrong, the guidance is the most important content on
  // the page — it goes directly under the verdict, not below the diagnostics.
  const actionsFirst = data.verdict !== 'SAFE'

  return (
    <div className={`min-h-screen bg-gradient-to-b from-slate-50 via-white to-white transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-8 sm:py-12">
        {/* Back link */}
        <Link
          to="/tools"
          search={{ invitationcode: undefined, __clerk_ticket: undefined }}
          className="mb-6 inline-flex items-center gap-2 text-base text-slate-500 transition-colors hover:text-slate-700"
        >
          <FiArrowLeft className="w-4 h-4" />
          Back to Tools
        </Link>

        {/* Sections */}
        <div className="space-y-6">
          {/* 1. Verdict Hero */}
          <VerdictHero
            verdict={data.verdict}
            status={data.status}
            riskScore={data.riskScore}
            headline={data.headline}
            explanation={data.explanation}
            factors={data.factors}
            inputType={data.inputType}
            screenshotUrl={data.screenshotUrl}
          />

          {/* 1b. Action steps lead when the verdict warrants action */}
          {actionsFirst && <ActionStepsSection actions={data.actions} animationDelay="0.1s" />}

          {/* 2. Submitted URL (URL mode only) */}
          {data.inputType === 'URL' && (
            <SubmittedUrlCard
              inputValue={data.inputValue}
              highlights={data.urlHighlights}
              finalUrl={data.finalUrl}
              domain={data.domain}
              screenshotUrl={data.screenshotUrl}
            />
          )}

          {/* 2a. What we looked at: the case restated, then the check ledger (URL mode only) */}
          {data.inputType === 'URL' && <ObservationSection observation={data.observation} />}

          {/* 2b. Uploaded image with context (IMAGE mode only) */}
          {data.inputType === 'IMAGE' && (
            <div className="animate-fade-up px-1" style={{ animationDelay: '0.1s' }}>
              {(data.imageContext?.imageDescription || data.imageContext?.requestedAction) && (
                <div>
                  <h2 className="mb-4 text-xl font-semibold text-slate-900 sm:text-2xl">Image summary</h2>
                  {data.imageContext?.imageDescription && (
                    <p className="text-base leading-relaxed text-slate-700">
                      <span className="font-medium text-slate-900">What we see: </span>
                      {data.imageContext.imageDescription}
                    </p>
                  )}
                  {data.imageContext?.requestedAction && (
                    <div className="mt-2 flex items-start gap-2">
                      <FiFlag className="mt-1 h-4 w-4 flex-shrink-0 text-amber-600" />
                      <p className="text-base text-slate-700">
                        <span className="font-medium text-slate-900">What it’s asking you to do: </span>
                        {data.imageContext.requestedAction}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* 2c. Message context (TEXT/EMAIL mode only) */}
          {(data.inputType === 'TEXT' || data.inputType === 'EMAIL') && data.messageContext && (
            <div className="animate-fade-up px-1" style={{ animationDelay: '0.1s' }}>
              <h2 className="mb-4 text-xl font-semibold text-slate-900 sm:text-2xl">Message analysis</h2>
              <div className="space-y-3">
                {/* Message type + claimed sender */}
                <div className="flex flex-wrap items-center gap-2">
                  {data.messageContext.messageType && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-600">
                      {data.messageContext.messageType === 'email' && <FiMail className="h-3.5 w-3.5" />}
                      {data.messageContext.messageType === 'sms' && <FiSmartphone className="h-3.5 w-3.5" />}
                      {(data.messageContext.messageType === 'chat' || data.messageContext.messageType === 'social_media') && <FiMessageSquare className="h-3.5 w-3.5" />}
                      {data.messageContext.messageType === 'email' ? 'Email' : data.messageContext.messageType === 'sms' ? 'Text message' : data.messageContext.messageType === 'chat' ? 'Chat message' : data.messageContext.messageType === 'social_media' ? 'Social media' : data.messageContext.messageType === 'letter_transcript' ? 'Letter' : data.messageContext.messageType === 'notification' ? 'Notification' : 'Message'}
                    </span>
                  )}
                  {data.messageContext.claimedSender && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-sm font-medium text-slate-600">
                      <FiUser className="h-3.5 w-3.5" />
                      Claims to be from: {data.messageContext.claimedSender}
                      {data.messageContext.senderSeemsLegitimate === true && (
                        <FiCheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                      )}
                      {data.messageContext.senderSeemsLegitimate === false && (
                        <FiAlertTriangle className="h-3.5 w-3.5 text-red-500" />
                      )}
                    </span>
                  )}
                </div>

                {/* Summary */}
                {data.messageContext.messageSummary && (
                  <p className="text-base leading-relaxed text-slate-700">
                    <span className="font-medium text-slate-900">What this message is about: </span>
                    {data.messageContext.messageSummary}
                  </p>
                )}

                {/* Requested action */}
                {data.messageContext.requestedAction && (
                  <div className="flex items-start gap-2">
                    <FiFlag className="mt-1 h-4 w-4 flex-shrink-0 text-amber-600" />
                    <p className="text-base text-slate-700">
                      <span className="font-medium text-slate-900">What it is asking you to do: </span>
                      {data.messageContext.requestedAction}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* 2d. Video context (VIDEO mode only) */}
          {data.inputType === 'VIDEO' && data.videoContext && (
            <VideoAnalysisSection
              videoContext={data.videoContext}
              flags={data.flags}
              animationDelay="0.1s"
            />
          )}

          {/* 3. Red Flags */}
          <RedFlagsSection flags={data.flags} />

          {/* 3b. Trust Signals (positive indicators) */}
          <TrustSignalsSection
            trustSignals={data.trustSignals ?? []}
            reputationContext={data.reputationContext ?? null}
          />

          {/* 4. Brand Comparison (conditional) */}
          {data.brandComparison?.detected && (
            <BrandComparison comparison={data.brandComparison} />
          )}

          {/* 5. Action steps close the page for SAFE results */}
          {!actionsFirst && <ActionStepsSection actions={data.actions} />}

          {/* 6. More options */}
          <div className="animate-fade-up" style={{ animationDelay: '0.35s' }}>
            <h2 className="mb-4 text-xl font-semibold text-slate-900 sm:text-2xl">More options</h2>
            <ShareReportRow
              checkId={data.id}
              onRerun={handleRerun}
              onRequestExpertReview={handleRequestExpertReview}
              isRequestingExpertReview={expertReviewMutation.isPending}
              expertReviewRequested={expertReviewRequested}
            />
          </div>

        </div>

        {/* Timestamp */}
        <p className="mt-8 text-center text-sm text-slate-400">
          Checked on {new Date(data.createdAt).toLocaleDateString('en-GB', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })}
        </p>
      </div>

      <ExpertReviewRequestModal
        open={showExpertModal}
        isSubmitting={expertReviewMutation.isPending}
        isSubmitted={expertReviewRequested}
        errorMessage={expertReviewError}
        onClose={() => {
          if (!expertReviewMutation.isPending) {
            setShowExpertModal(false)
            setExpertReviewError(null)
          }
        }}
        onConfirm={handleConfirmExpertReview}
      />
    </div>
  )
}
